import React from "react";
import LegalDocument from "./LegalDocument";

/* =========================================================================
 *  Apsession Organizer Agreement — verbatim from the signed PDF
 *  (Effective 1 September 2026).
 *
 *  Layout, table of contents, and cross-document linking come from
 *  <LegalDocument>; this file is content only.
 * ========================================================================= */

export const ORGANIZER_EFFECTIVE_DATE = "September 1, 2026";
export const ORGANIZER_LAST_UPDATED = "September 1, 2026";

const INTRO = [
    "This Apsession Organizer Agreement (“Organizer Agreement”) governs the use of Apsession's event creation, ticketing, payment, sales, distribution, management, and related services by Event Organizers.",
    "This Agreement is entered into between Apsession Limited, a company operating in Jamaica (“Apsession,” “we,” “us,” or “our”), and the individual, business, organization, promoter, producer, venue, or other entity creating, publishing, managing, or selling tickets for an event through Apsession (“Organizer,” “you,” or “your”).",
    "By creating or publishing an event, selling tickets, accepting payments, receiving payouts, inviting managers or ticket sellers, or otherwise using Apsession's Organizer Services, you agree to this Organizer Agreement, the Apsession Terms of Service, the Apsession Privacy Policy, and applicable service-specific terms.",
    "If you accept this Agreement on behalf of an organization or legal entity, you represent that you have authority to bind that entity.",
];

const SECTIONS = [
    {
        id: "apsessions-role",
        title: "1. Apsession's Role",
        blocks: [
            { p: "Apsession provides technology and related services enabling Organizers to:" },
            {
                list: [
                    "Create and publish events;",
                    "Create ticket types and tiers;",
                    "Sell paid and free tickets;",
                    "Accept online ticket payments;",
                    "Record eligible cash and Gate sales;",
                    "Issue complimentary tickets;",
                    "Deliver digital tickets;",
                    "Scan and validate tickets;",
                    "Assign Event Managers and Gate Staff;",
                    "Work with Verified Ticket Ambassadors;",
                    "Work with Digital Ticket Outlets;",
                    "Track sales;",
                    "Receive event analytics;",
                    "Receive eligible event payouts; and",
                    "Purchase optional marketing and sales services.",
                ],
            },
            { p: "Unless expressly stated otherwise, Apsession is a ticketing and technology provider and is not the producer, promoter, venue operator, or Organizer of the Organizer's event.", strong: true },
            { p: "The Organizer remains responsible for producing and delivering the event." },
        ],
    },
    {
        id: "organizer-eligibility",
        title: "2. Organizer Eligibility",
        blocks: [
            { p: "To use Apsession as an Organizer, you must:" },
            {
                list: [
                    "Be legally capable of entering into this Agreement;",
                    "Provide accurate information;",
                    "Have authority to organize or represent the event;",
                    "Have the legal right to sell tickets;",
                    "Obtain required licences, permissions, approvals, and permits; and",
                    "Comply with applicable laws.",
                ],
            },
        ],
    },
    {
        id: "organizer-verification",
        title: "3. Organizer Verification",
        blocks: [
            { p: "Apsession may require information or documentation including:" },
            {
                list: [
                    "Legal name;",
                    "Government identification;",
                    "Business information;",
                    "Address;",
                    "Contact information;",
                    "Bank information;",
                    "Tax information where applicable;",
                    "Venue information;",
                    "Event contracts;",
                    "Proof of authorization;",
                    "Previous event history; and",
                    "Other information reasonably required to assess an account or event.",
                ],
            },
            { p: "Apsession may suspend event approval, sales, or payouts until verification is complete." },
            { p: "False, altered, fraudulent, or materially misleading documentation may result in suspension or termination." },
        ],
    },
    {
        id: "event-submission-approval",
        title: "4. Event Submission and Approval",
        blocks: [
            { p: "Events submitted to Apsession may be reviewed before or after publication." },
            { p: "Apsession may:" },
            {
                list: [
                    "Approve an event;",
                    "Reject an event;",
                    "Request additional information;",
                    "Require modifications;",
                    "Suspend an event;",
                    "Unpublish an event; or",
                    "Remove an event.",
                ],
            },
            { p: "Approval does not constitute sponsorship, endorsement, or a guarantee by Apsession." },
        ],
    },
    {
        id: "organizer-responsibilities",
        title: "5. Organizer Responsibilities",
        blocks: [
            { p: "The Organizer is responsible for the production and operation of the event, including:" },
            {
                list: [
                    "Venue;",
                    "Artists and entertainment;",
                    "Event staffing;",
                    "Security;",
                    "Licences and permits;",
                    "Insurance;",
                    "Capacity limits;",
                    "Age restrictions;",
                    "Health and safety;",
                    "Event schedules;",
                    "Admission procedures;",
                    "Customer experience; and",
                    "Compliance with applicable law.",
                ],
            },
        ],
    },
    {
        id: "event-information",
        title: "6. Event Information",
        blocks: [
            { p: "The Organizer must accurately disclose material information including:" },
            {
                list: [
                    "Event name;",
                    "Event description;",
                    "Event date and time;",
                    "Venue;",
                    "Ticket categories;",
                    "Ticket pricing;",
                    "Capacity;",
                    "Age restrictions;",
                    "Admission conditions;",
                    "Refund policy; and",
                    "Other material restrictions.",
                ],
            },
            { p: "Material changes must be promptly updated or reported to Apsession." },
        ],
    },
    {
        id: "inventory-and-capacity",
        title: "7. Ticket Inventory and Capacity",
        blocks: [
            { p: "The Organizer is responsible for setting appropriate ticket inventory." },
            { p: "The Organizer must not knowingly sell more tickets than permitted by venue, legal, contractual, or safety capacity limits." },
            { p: "Apsession is not responsible for overselling caused by tickets sold outside Apsession or incorrect capacity information supplied by the Organizer." },
        ],
    },
    {
        id: "ticket-pricing",
        title: "8. Ticket Pricing",
        blocks: [
            { p: "The Organizer determines its base ticket pricing unless otherwise agreed." },
            { p: "Ticket pricing must not be misleading." },
            { p: "Once purchased, a ticket price may not be retroactively increased for the applicable purchaser." },
        ],
    },
    {
        id: "apsession-fees",
        title: "9. Apsession Fees",
        blocks: [
            { p: "Apsession may charge fees including:" },
            {
                list: [
                    "Service fees;",
                    "Processing fees;",
                    "Ticketing fees;",
                    "Ambassador-related fees;",
                    "Digital Ticket Outlet fees;",
                    "Marketing fees;",
                    "Upgrade fees; and",
                    "Custom service fees.",
                ],
            },
            { p: "Fees may vary by event, transaction, channel, country, payment method, Organizer, or service." },
            { p: "The pricing shown to the Organizer or separately agreed with Apsession forms part of this Agreement." },
        ],
    },
    {
        id: "fee-configurations",
        title: "10. Fee Configurations",
        blocks: [
            { p: "Where available, Organizers may select arrangements such as:" },
            {
                defs: [
                    ["Buyer Pays All", "eligible fees are added to the purchaser's amount."],
                    ["Organizer Pays All", "eligible fees are deducted from Organizer proceeds."],
                    ["Split Fees", "fees are allocated between Organizer and purchaser."],
                    ["Custom Pricing", "qualifying events may receive separately negotiated pricing."],
                ],
            },
            { p: "The Organizer is responsible for confirming its selected configuration before ticket sales begin." },
        ],
    },
    {
        id: "online-ticket-sales",
        title: "11. Online Ticket Sales",
        blocks: [
            { p: "Apsession or its payment partners may collect online payments from ticket purchasers." },
            { p: "Amounts collected may include:" },
            {
                list: [
                    "Ticket price;",
                    "Service fees;",
                    "Processing fees;",
                    "Taxes; and",
                    "Other disclosed charges.",
                ],
            },
            { p: "Gross transaction amounts are not necessarily equal to Organizer earnings." },
            { p: "Organizer earnings are calculated after applicable deductions, including fees, refunds, reversals, commissions, chargebacks, and adjustments." },
        ],
    },
    {
        id: "cash-and-gate-sales",
        title: "12. Cash and Gate Sales",
        blocks: [
            { p: "Where supported, authorized users may record cash or Gate sales through Apsession." },
            { p: "The Organizer is responsible for:" },
            {
                list: [
                    "Safeguarding cash;",
                    "Ensuring sales are accurately recorded;",
                    "Reconciling transactions;",
                    "Supervising sellers;",
                    "Providing any required access bands or other event credentials; and",
                    "Paying amounts due to Apsession.",
                ],
            },
            { p: "Apsession is not responsible for cash lost, stolen, misappropriated, or incorrectly handled by the Organizer or the Organizer's personnel." },
        ],
    },
    {
        id: "digital-ticket-outlets",
        title: "13. Digital Ticket Outlets",
        blocks: [
            { p: "The Organizer may make tickets available through authorized Apsession Digital Ticket Outlets." },
            { p: "Outlet transactions may be subject to separate fees, commissions, settlement processes, and remittance obligations." },
        ],
    },
    {
        id: "verified-ambassadors",
        title: "14. Verified Ticket Ambassadors",
        blocks: [
            { p: "Apsession may provide Organizers access to Verified Ticket Ambassadors." },
            { p: "Eligible Ambassadors may request authorization to sell tickets." },
            { p: "Organizers may approve or reject such requests." },
            { p: "Verification does not guarantee an Ambassador's performance, conduct, sales volume, or financial reliability." },
        ],
    },
    {
        id: "ambassador-commissions",
        title: "15. Ambassador Commissions",
        blocks: [
            { p: "The Organizer may establish commissions for eligible Ambassador sales." },
            { p: "Apsession may calculate and allocate applicable commission amounts." },
            { p: "Commission eligibility may be adjusted for:" },
            {
                list: [
                    "Refunds;",
                    "Voided tickets;",
                    "Chargebacks;",
                    "Fraud;",
                    "Reversals; or",
                    "Cancelled events.",
                ],
            },
        ],
    },
    {
        id: "event-managers",
        title: "16. Event Managers and Authorized Users",
        blocks: [
            { p: "The Organizer may grant other users event permissions." },
            { p: "These users may be able to:" },
            {
                list: [
                    "Manage events;",
                    "View sales;",
                    "Sell tickets;",
                    "Manage inventory;",
                    "View relevant customer information;",
                    "Access reports; or",
                    "Perform other authorized actions.",
                ],
            },
            { p: "Actions performed by authorized users may be treated as authorized actions of the Organizer." },
            { p: "The Organizer must remove permissions when access is no longer appropriate." },
        ],
    },
    {
        id: "gate-staff",
        title: "17. Gate Staff",
        blocks: [
            { p: "The Organizer may authorize Gate Staff with permissions including:" },
            {
                list: [
                    "Ticket Scanner Only",
                    "Sell Gate Tickets Only",
                    "Both",
                ],
            },
            { p: "The Organizer is responsible for selecting appropriate permissions, supervising Gate Staff, safeguarding cash, and controlling access to Apsession systems." },
        ],
    },
    {
        id: "ticket-scanning",
        title: "18. Ticket Scanning",
        blocks: [
            { p: "The Organizer is responsible for:" },
            {
                list: [
                    "Controlling scanning devices;",
                    "Training scanners;",
                    "Following recommended procedures;",
                    "Preventing unauthorized admission; and",
                    "Maintaining appropriate event security.",
                ],
            },
            { p: "Ticket scanning validates a ticket credential but does not independently verify an attendee's identity, age, or entitlement to enter." },
        ],
    },
    {
        id: "offline-scanning",
        title: "19. Offline Ticket Scanning",
        blocks: [
            { p: "Apsession may enable tickets to be scanned without an internet connection." },
            { p: "The Organizer acknowledges that real-time synchronization may not be possible while devices are offline." },
            { p: "The Organizer should synchronize devices whenever connectivity becomes available." },
            { p: "Apsession is not responsible for duplicate entry or scanning inconsistencies caused by misuse, failure to synchronize, unauthorized scanning devices, or failure to follow recommended procedures." },
        ],
    },
    {
        id: "complimentary-tickets",
        title: "20. Complimentary and Free Tickets",
        blocks: [
            { p: "Apsession may allow complimentary or free tickets." },
            { p: "Such tickets may still be subject to platform restrictions, delivery costs, upgrades, validation requirements, or other disclosed conditions." },
        ],
    },
    {
        id: "digital-ticket-delivery",
        title: "21. Digital Ticket Delivery",
        blocks: [
            { p: "Tickets may be delivered through:" },
            {
                list: [
                    "Email;",
                    "SMS;",
                    "WhatsApp;",
                    "Ticket links;",
                    "In-app delivery; or",
                    "Other supported electronic methods.",
                ],
            },
            { p: "The Organizer authorizes Apsession to communicate with purchasers and attendees as reasonably necessary to administer ticket transactions and events." },
        ],
    },
    {
        id: "transaction-records",
        title: "22. Transaction Records",
        blocks: [
            { p: "Apsession may maintain electronic records including:" },
            {
                list: [
                    "Ticket prices;",
                    "Quantities;",
                    "Fees;",
                    "Sales channels;",
                    "Sellers;",
                    "Payments;",
                    "Refunds;",
                    "Chargebacks;",
                    "Commissions;",
                    "Organizer earnings;",
                    "Scans; and",
                    "Settlement information.",
                ],
            },
            { p: "Apsession's platform records may be relied upon as evidence of transactions, subject to correction of documented errors." },
        ],
    },
    {
        id: "organizer-payouts",
        title: "23. Organizer Payouts",
        blocks: [
            { p: "Eligible Organizer proceeds may be paid through direct deposit or another supported payment method." },
            { p: "Unless another arrangement applies, standard eligible online event proceeds may be processed approximately five (5) business days after the event ends." },
            { p: "Banking and payment-network processing times may cause additional delays." },
            { p: "A displayed payout date is an estimate rather than an unconditional guarantee." },
        ],
    },
    {
        id: "scheduled-payouts",
        title: "24. Pre-Event and Scheduled Payouts",
        blocks: [
            { p: "Apsession may approve pre-event or scheduled payouts for selected events." },
            { p: "Eligibility may depend on:" },
            {
                list: [
                    "Organizer history;",
                    "Sales volume;",
                    "Refund history;",
                    "Chargeback history;",
                    "Event risk;",
                    "Verification;",
                    "Event timing; and",
                    "Settlement status.",
                ],
            },
            { p: "Pre-event payouts are not guaranteed." },
        ],
    },
    {
        id: "holds-and-reserves",
        title: "25. Payout Holds and Reserves",
        blocks: [
            { p: "Apsession may delay, withhold, reduce, or establish a reserve against Organizer proceeds where reasonably necessary." },
            { p: "Reasons may include:" },
            {
                list: [
                    "Event cancellation risk;",
                    "Postponement;",
                    "Significant event changes;",
                    "Refund activity;",
                    "Chargebacks;",
                    "Fraud;",
                    "Unusual transaction activity;",
                    "Customer complaints;",
                    "Verification issues;",
                    "Legal requirements;",
                    "Payment-provider requirements;",
                    "Negative balances; or",
                    "Reasonable concern that an event may not occur as advertised.",
                ],
            },
            { p: "A reserve may remain until the applicable risk has reasonably been resolved." },
        ],
    },
    {
        id: "right-of-set-off",
        title: "26. Right of Set-Off",
        blocks: [
            { p: "To the extent permitted by law, the Organizer authorizes Apsession to deduct amounts owed by the Organizer from funds otherwise payable to that Organizer." },
            { p: "Deductions may include:" },
            {
                list: [
                    "Refunds;",
                    "Chargebacks;",
                    "Reversals;",
                    "Processing fees;",
                    "Apsession fees;",
                    "Commissions;",
                    "Outlet-related amounts;",
                    "Marketing purchases;",
                    "Negative balances; and",
                    "Other amounts properly owed under this Agreement.",
                ],
            },
            { p: "Where appropriate, Apsession may apply earnings from one event toward obligations arising from another event operated by the same Organizer." },
        ],
    },
    {
        id: "negative-balances",
        title: "27. Negative Balances",
        blocks: [
            { p: "An Organizer may develop a negative account balance when deductions exceed available earnings." },
            { p: "The Organizer remains responsible for that amount." },
            { p: "Apsession may recover a negative balance from:" },
            {
                list: [
                    "Future ticket sales;",
                    "Future event earnings;",
                    "Available account balances;",
                    "Other payouts;",
                    "Authorized payment methods; or",
                    "Direct repayment.",
                ],
            },
            { p: "Apsession may suspend sales or payouts until an outstanding balance is resolved." },
        ],
    },
    {
        id: "refunds",
        title: "28. Refunds",
        blocks: [
            { p: "The Organizer is responsible for its event refund policy, subject to applicable law and Apsession requirements." },
            { p: "The Organizer authorizes Apsession to deduct refund amounts from:" },
            {
                list: [
                    "Unpaid ticket proceeds;",
                    "Event earnings;",
                    "Reserves;",
                    "Future payouts; or",
                    "Other funds payable to the Organizer.",
                ],
            },
            { p: "If available funds are insufficient, the Organizer remains responsible for the outstanding amount." },
        ],
    },
    {
        id: "cancelled-events",
        title: "29. Cancelled Events",
        blocks: [
            { p: "The Organizer must notify Apsession immediately if an event is cancelled." },
            { p: "Apsession may:" },
            {
                list: [
                    "Stop ticket sales;",
                    "Hold payouts;",
                    "Establish or increase reserves;",
                    "Notify ticket purchasers;",
                    "Process eligible refunds; and",
                    "Deduct refunds from Organizer funds.",
                ],
            },
            { p: "If event proceeds have already been paid to the Organizer, the Organizer must return amounts reasonably required to satisfy applicable refunds, reversals, or related obligations." },
        ],
    },
    {
        id: "postponed-events",
        title: "30. Postponed Events",
        blocks: [
            { p: "The Organizer must promptly notify Apsession of postponements." },
            { p: "Apsession may:" },
            {
                list: [
                    "Suspend ticket sales;",
                    "Notify purchasers;",
                    "Suspend payouts;",
                    "Hold funds;",
                    "Request further information; or",
                    "Facilitate or require refunds where appropriate.",
                ],
            },
            { p: "A substantial or indefinite postponement may be treated similarly to a cancellation where reasonably appropriate." },
        ],
    },
    {
        id: "material-event-changes",
        title: "31. Material Event Changes",
        blocks: [
            { p: "The Organizer must promptly disclose significant changes such as:" },
            {
                list: [
                    "Venue changes;",
                    "Date changes;",
                    "Significant time changes;",
                    "Major advertised performer cancellations; or",
                    "Material reductions in advertised event offerings.",
                ],
            },
            { p: "Apsession may communicate such changes to purchasers and take reasonable measures to address associated refund and transaction risk." },
        ],
    },
    {
        id: "chargebacks",
        title: "32. Chargebacks",
        blocks: [
            { p: "A chargeback occurs when a purchaser disputes a transaction through a bank, card issuer, or payment provider." },
            { p: "Chargebacks may occur after funds have already been paid to the Organizer." },
            { p: "To the extent permitted by law, the Organizer is financially responsible for chargebacks attributable to the Organizer's event." },
            { p: "Apsession may deduct:" },
            {
                list: [
                    "The disputed amount;",
                    "Processor charges;",
                    "Chargeback fees; and",
                    "Directly related amounts",
                ],
            },
            { p: "from Organizer funds." },
        ],
    },
    {
        id: "chargeback-cooperation",
        title: "33. Chargeback Cooperation",
        blocks: [
            { p: "The Organizer agrees to reasonably assist Apsession with chargeback disputes." },
            { p: "Apsession may request:" },
            {
                list: [
                    "Proof the event occurred;",
                    "Ticket scanning records;",
                    "Event photographs;",
                    "Purchaser communications;",
                    "Event policies;",
                    "Venue documents; or",
                    "Other relevant evidence.",
                ],
            },
            { p: "Apsession does not guarantee that a payment dispute will be successfully challenged." },
        ],
    },
    {
        id: "fraud-and-suspicious-transactions",
        title: "34. Fraud and Suspicious Transactions",
        blocks: [
            { p: "Apsession may monitor transactions for suspicious or fraudulent activity." },
            { p: "Apsession may:" },
            {
                list: [
                    "Decline transactions;",
                    "Void tickets;",
                    "Request additional verification;",
                    "Suspend sales;",
                    "Hold payouts;",
                    "Establish reserves;",
                    "Reverse suspicious transactions; or",
                    "Restrict accounts",
                ],
            },
            { p: "where reasonably necessary to protect users, Apsession, or payment partners." },
        ],
    },
    {
        id: "remittance-of-funds",
        title: "35. Remittance of Funds",
        blocks: [
            { p: "Certain Organizers or persons operating under Organizer authority may collect funds owed to Apsession." },
            { p: "Amounts due must be remitted according to the applicable deadline and instructions." },
            { p: "Amounts collected on behalf of Apsession are not Organizer earnings." },
            { p: "Failure to remit funds may result in:" },
            {
                list: [
                    "Suspension;",
                    "Withheld payouts;",
                    "Deduction from other earnings;",
                    "Loss of selling privileges;",
                    "Collection activity; or",
                    "Legal action.",
                ],
            },
        ],
    },
    {
        id: "event-marketing-services",
        title: "36. Event Marketing Services",
        blocks: [
            { p: "Organizers may purchase optional Apsession marketing services." },
            { p: "Purchase of marketing services does not guarantee:" },
            {
                list: [
                    "Ticket sales;",
                    "Attendance;",
                    "Revenue;",
                    "Engagement; or",
                    "Event success",
                ],
            },
            { p: "unless Apsession expressly guarantees a particular result in writing." },
        ],
    },
    {
        id: "organizer-content",
        title: "37. Organizer Content",
        blocks: [
            { p: "The Organizer may provide promotional materials including:" },
            {
                list: [
                    "Event names;",
                    "Logos;",
                    "Flyers;",
                    "Photographs;",
                    "Videos;",
                    "Artist images; and",
                    "Descriptions.",
                ],
            },
            { p: "The Organizer represents that it owns or has permission to use such content." },
            { p: "The Organizer grants Apsession a non-exclusive, worldwide, royalty-free licence to host, reproduce, display, resize, format, distribute, promote, and otherwise use event content as reasonably necessary to provide the Services and promote the event or Apsession." },
        ],
    },
    {
        id: "ip-claims",
        title: "38. Intellectual Property Claims",
        blocks: [
            { p: "Organizers must not provide material that infringes another person's copyright, trademark, privacy, publicity, or other rights." },
            { p: "Apsession may remove disputed material while investigating complaints." },
        ],
    },
    {
        id: "customer-information",
        title: "39. Customer Information",
        blocks: [
            { p: "Organizers may receive access to purchaser and attendee information needed to administer their events." },
            { p: "Such information may be used for legitimate purposes including:" },
            {
                list: [
                    "Event administration;",
                    "Customer service;",
                    "Event updates;",
                    "Entry management; and",
                    "Other legally permitted event communications.",
                ],
            },
            { p: "The Organizer must not improperly sell, disclose, misuse, or provide customer information to unauthorized third parties." },
        ],
    },
    {
        id: "data-protection",
        title: "40. Data Protection",
        blocks: [
            { p: "The Organizer must comply with applicable privacy and data-protection obligations when processing personal information received through Apsession." },
            { p: "The Organizer may have independent legal obligations regarding such information." },
        ],
    },
    {
        id: "event-safety",
        title: "41. Event Safety",
        blocks: [
            { p: "The Organizer is responsible for appropriate event safety and security measures, including where applicable:" },
            {
                list: [
                    "Security personnel;",
                    "Emergency procedures;",
                    "Crowd management;",
                    "Capacity controls;",
                    "Medical support;",
                    "Age verification;",
                    "Alcohol controls;",
                    "Venue safety; and",
                    "Required licences and permits.",
                ],
            },
            { p: "Apsession's ticketing technology does not transfer responsibility for event safety to Apsession." },
        ],
    },
    {
        id: "prohibited-events",
        title: "42. Prohibited Events and Activities",
        blocks: [
            { p: "An Organizer may not use Apsession for an event or activity that:" },
            {
                list: [
                    "Violates applicable law;",
                    "Is fraudulent;",
                    "Materially misrepresents the event;",
                    "Infringes third-party rights;",
                    "Creates unreasonable risk to Apsession or customers;",
                    "Involves unauthorized ticket sales; or",
                    "Facilitates financial crime.",
                ],
            },
        ],
    },
    {
        id: "organizer-representations",
        title: "43. Organizer Representations",
        blocks: [
            { p: "The Organizer represents and warrants that:" },
            {
                olist: [
                    "Information supplied to Apsession is materially accurate.",
                    "The Organizer has authority to operate the event.",
                    "The Organizer has the necessary rights to sell tickets.",
                    "Organizer content does not knowingly violate third-party rights.",
                    "The Organizer will comply with applicable law.",
                    "The Organizer will substantially deliver the event as advertised or appropriately address affected purchasers.",
                    "The Organizer will satisfy financial obligations arising from its event.",
                ],
            },
        ],
    },
    {
        id: "indemnification",
        title: "44. Indemnification",
        blocks: [
            { p: "To the extent permitted by law, the Organizer agrees to indemnify and hold harmless Apsession, its affiliates, officers, employees, contractors, and agents from third-party claims, losses, liabilities, damages, and reasonable expenses arising from:" },
            {
                list: [
                    "The Organizer's event;",
                    "Event cancellation;",
                    "Event operations;",
                    "Venue conditions;",
                    "Organizer content;",
                    "Organizer misconduct;",
                    "Breach of this Agreement;",
                    "Violation of law;",
                    "Intellectual-property infringement; or",
                    "Acts of persons operating under the Organizer's authority.",
                ],
            },
            { p: "This provision does not require the Organizer to indemnify Apsession for liability that applicable law does not permit to be transferred." },
        ],
    },
    {
        id: "limitation-of-liability",
        title: "45. Limitation of Liability",
        blocks: [
            { p: "To the maximum extent permitted by law, Apsession is not responsible for indirect, incidental, special, consequential, exemplary, or punitive losses arising from circumstances outside its reasonable control." },
            { p: "Apsession is not the insurer of an Organizer's event." },
            { p: "Nothing in this Agreement excludes liability that cannot lawfully be excluded." },
        ],
    },
    {
        id: "no-guarantee-of-sales",
        title: "46. No Guarantee of Sales",
        blocks: [
            { p: "Apsession does not guarantee:" },
            {
                list: [
                    "Minimum ticket sales;",
                    "Profitability;",
                    "Attendance;",
                    "Ambassador performance;",
                    "Outlet performance;",
                    "Marketing results; or",
                    "Organizer revenue.",
                ],
            },
        ],
    },
    {
        id: "account-suspension",
        title: "47. Account Suspension",
        blocks: [
            { p: "Apsession may suspend an Organizer account or event where reasonably necessary because of:" },
            {
                list: [
                    "Fraud;",
                    "Excessive chargebacks;",
                    "Unpaid balances;",
                    "Failure to remit funds;",
                    "Event complaints;",
                    "Misrepresentation;",
                    "Security concerns;",
                    "Legal requirements; or",
                    "Violation of this Agreement.",
                ],
            },
            { p: "Suspension may restrict event creation, sales, payouts, ticket issuance, seller access, or account access." },
        ],
    },
    {
        id: "termination",
        title: "48. Termination",
        blocks: [
            { p: "Either party may discontinue applicable Services subject to outstanding obligations." },
            { p: "Termination does not eliminate existing responsibilities relating to:" },
            {
                list: [
                    "Refunds;",
                    "Chargebacks;",
                    "Negative balances;",
                    "Remittances;",
                    "Fees;",
                    "Customer obligations; or",
                    "Other amounts properly due.",
                ],
            },
            { p: "Apsession may retain funds reasonably required to address unresolved obligations." },
        ],
    },
    {
        id: "survival",
        title: "49. Survival",
        blocks: [
            { p: "Provisions relating to payments, refunds, chargebacks, negative balances, remittances, intellectual property, privacy, indemnification, liability, and disputes survive termination where necessary to give them effect." },
        ],
    },
    {
        id: "changes-to-agreement",
        title: "50. Changes to this Agreement",
        blocks: [
            { p: "Apsession may update this Organizer Agreement." },
            { p: "Material changes may be communicated through:" },
            {
                list: [
                    "Email;",
                    "Organizer dashboard;",
                    "Mobile application;",
                    "Website; or",
                    "Other reasonable electronic means.",
                ],
            },
        ],
    },
    {
        id: "relationship-of-parties",
        title: "51. Relationship of the Parties",
        blocks: [
            { p: "Nothing in this Agreement creates a partnership, joint venture, franchise, employment relationship, or general agency relationship between Apsession and the Organizer." },
            { p: "The Organizer operates independently from Apsession." },
        ],
    },
    {
        id: "electronic-communications",
        title: "52. Electronic Communications",
        blocks: [
            { p: "The Organizer agrees that Apsession may provide agreements, notices, payout information, transaction information, account alerts, and related communications electronically." },
        ],
    },
    {
        id: "governing-law",
        title: "53. Governing Law",
        blocks: [
            { p: "This Organizer Agreement is governed by and interpreted in accordance with the laws of Jamaica." },
        ],
    },
    {
        id: "dispute-resolution",
        title: "54. Dispute Resolution",
        blocks: [
            { p: "The Organizer should first contact Apsession and provide reasonable information regarding any dispute so the parties can attempt an informal resolution." },
            { p: "If the dispute cannot be resolved, either party may pursue remedies available under applicable Jamaican law before a court or competent authority." },
        ],
    },
    {
        id: "severability",
        title: "55. Severability",
        blocks: [
            { p: "If any provision is determined to be invalid or unenforceable, the remaining provisions continue in effect to the extent permitted by law." },
        ],
    },
    {
        id: "no-waiver",
        title: "56. No Waiver",
        blocks: [
            { p: "Apsession's failure to immediately enforce a provision does not waive the right to enforce it later." },
        ],
    },
    {
        id: "entire-agreement",
        title: "57. Entire Agreement",
        blocks: [
            { p: "This Organizer Agreement, together with:" },
            {
                list: [
                    "The Apsession Terms of Service;",
                    "The Apsession Privacy Policy;",
                    "Applicable pricing;",
                    "Event-specific terms; and",
                    "Specifically negotiated written agreements",
                ],
            },
            { p: "constitutes the agreement governing the Organizer's use of Apsession Organizer Services." },
            { p: "Where a specifically negotiated written agreement conflicts with this Organizer Agreement, the specifically negotiated term will control to the extent of that conflict." },
        ],
    },
    {
        id: "organizer-acceptance",
        title: "58. Organizer Acceptance",
        blocks: [
            { p: "By selecting “I Agree,” “Create Event,” “Publish Event,” “Start Selling,” or another acceptance mechanism presented by Apsession, or by continuing to use Organizer Services after being presented with this Agreement, the Organizer confirms that:" },
            {
                list: [
                    "The Organizer has read this Agreement;",
                    "The Organizer understands it;",
                    "The Organizer has authority to enter into it;",
                    "The Organizer agrees to the Apsession Terms of Service and Privacy Policy; and",
                    "The Organizer agrees to be bound by this Organizer Agreement.",
                ],
            },
        ],
    },
    {
        id: "contact",
        title: "59. Contact Apsession",
        blocks: [
            { contact: true },
        ],
    },
];

export default function OrganizerAgreement() {
    return (
        <LegalDocument
            title="Organizer Agreement"
            path="/organizer-agreement"
            effectiveDate={ORGANIZER_EFFECTIVE_DATE}
            lastUpdated={ORGANIZER_LAST_UPDATED}
            intro={INTRO}
            sections={SECTIONS}
        />
    );
}
